var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Trans, t } from '@lingui/macro';
import { AvatarLetter, AvatarWithLabel, Button, Loader, TextArea, Typography, themeGet, } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import gql from 'graphql-tag';
import styled, { css } from 'styled-components';
import { useActiveDocument } from 'shared/domains/documents';
import { useUserName } from './useUserName';
import { Markdown } from '../Markdown';
// ---- GraphQL
const CHAT_WITH_DOCUMENT_REVIEW_MUTATION = gql `
  mutation ChatWithDocumentReview($messages: [ReviewChatMessageInput!]!, $feedbackId: ID!) {
    chatWithDocumentReview(messages: $messages, feedbackId: $feedbackId) {
      token
    }
  }
`;
const CHAT_WITH_REVIEW_PLAYBOOK_MUTATION = gql `
  mutation ChatPlaybook($messages: [ReviewChatMessageInput!]!, $documentId: ID!) {
    chatWithDocumentPlaybook(messages: $messages, documentId: $documentId) {
      token
    }
  }
`;
// ---- Hooks
// This implements a temporary chat system for the review chat.
// It maintains ephemeral client side chat message state which is sent to the server.
// The server is currently stateless in regards to review chat conversations.
const useChat = (chatRequest) => {
    const [messages, setMessages] = useState([]);
    // Functions derived from setMessages
    const appendToLastMessage = useCallback((text) => {
        setMessages(msgs => {
            const lastMessage = msgs[msgs.length - 1];
            if (lastMessage) {
                return [
                    ...msgs.slice(0, -1),
                    Object.assign(Object.assign({}, lastMessage), { message: lastMessage.message + text }),
                ];
            }
            return msgs;
        });
    }, [setMessages]);
    const setLastMessageDone = useCallback(() => {
        setMessages(msgs => {
            const lastMessage = msgs[msgs.length - 1];
            if (lastMessage) {
                return [
                    ...msgs.slice(0, -1),
                    Object.assign(Object.assign({}, lastMessage), { writing: false }),
                ];
            }
            return msgs;
        });
    }, [setMessages]);
    const appendMessages = useCallback((message) => setMessages(prev => [...prev, ...message]), [setMessages]);
    // TODO - CONFIG - ASK PREFERRED WAY
    const REVIEW_CHAT_URL = 'http://localhost:3135';
    return {
        messages,
        chat: useCallback((userPrompt) => __awaiter(void 0, void 0, void 0, function* () {
            // Append user message and system message target for chunk appending
            // When sending to API we only include the user message
            const newMessages = [
                {
                    role: 'user',
                    message: userPrompt,
                    writing: false,
                },
                {
                    role: 'assistant',
                    message: '',
                    writing: true,
                },
            ];
            appendMessages(newMessages);
            // Request chat message and retrieve token for chat stream
            // ! We include the user chat message, but not the assistant pending message
            const token = yield chatRequest([...messages, newMessages[0]]);
            if (token) {
                // Open SSE stream for chat messages
                const evtSource = new EventSource(`${REVIEW_CHAT_URL}/review-chat/${token}`);
                evtSource.onmessage = event => {
                    // TODO - ACTUALLY PARSE - ASK FOR ZOD
                    const [, [, content]] = JSON.parse(event.data);
                    appendToLastMessage(content);
                };
                // ! The server will close the connection after sending the end token,
                // ! this is treated as an error state by the EventSource, and we thus
                // ! close the connection.
                evtSource.onerror = () => {
                    evtSource.close();
                    setLastMessageDone();
                };
            }
        }), [appendMessages, chatRequest, messages, appendToLastMessage, setLastMessageDone]),
    };
};
// Specialized chat hooks for review chat
const useChatWithDocumentReview = (feedbackId) => {
    const [chatWithDocumentReview] = useMutation(CHAT_WITH_DOCUMENT_REVIEW_MUTATION);
    return useChat((messages) => chatWithDocumentReview({
        variables: {
            feedbackId,
            messages: messages.map(({ role, message }) => ({ role, message })),
        },
    }).then(res => { var _a, _b, _c; return (_c = (_b = (_a = res.data) === null || _a === void 0 ? void 0 : _a.chatWithDocumentReview) === null || _b === void 0 ? void 0 : _b.token) !== null && _c !== void 0 ? _c : ''; }));
};
// Specialized chat hooks for playbook chat
const useChatWithPlaybookReview = () => {
    const { id: documentId } = useActiveDocument();
    const [chatWithPlaybook] = useMutation(CHAT_WITH_REVIEW_PLAYBOOK_MUTATION);
    return useChat((messages) => chatWithPlaybook({
        variables: {
            documentId,
            messages: messages.map(({ role, message }) => ({ role, message })),
        },
    }).then(res => { var _a, _b, _c; return (_c = (_b = (_a = res.data) === null || _a === void 0 ? void 0 : _a.chatWithDocumentPlaybook) === null || _b === void 0 ? void 0 : _b.token) !== null && _c !== void 0 ? _c : ''; }));
};
// ---- StyledComponents
const Container = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${themeGet('spacing.lg')};
  padding: ${themeGet('spacing.xl')};
  height: 100%;
  overflow: hidden auto;
`;
const Thread = styled.div `
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${themeGet('spacing.3xl')};
  justify-content: flex-end;
`;
const Scroller = styled.div `
  display: flex;
  flex-direction: column;
  overflow: hidden auto;
  flex: 1;
`;
const StyledMarkdown = styled(Markdown) `
  display: inline;
  ${props => props.$writing &&
    css `
      & > *:last-child::after {
        content: '|';
        font-weight: ${themeGet('fontWeight.extrabold')};
        color: ${themeGet('colors.assistant.500')};
      }
    `}
`;
const Message = styled.div `
  display: flex;
  flex-direction: column;

  ${({ $direction }) => $direction === 'right' &&
    css `
      align-items: flex-end;
      text-align: right;
    `};
`;
const SuggestionsSpacer = styled.div `
  display: flex;
  gap: ${themeGet('spacing.md')};
  flex-flow: row wrap;
`;
const TextAreaSpacer = styled.div `
  display: flex;
  gap: ${themeGet('spacing.md')};
  flex-direction: column;
  align-items: end;
`;
const StyledAvatarWithLabel = styled(AvatarWithLabel).attrs({ size: 'md' }) `
  margin-bottom: ${themeGet('spacing.lg')};
`;
// ---- Components
// Internal common component
function Chat({ send, messages, userName, }) {
    const [userMessage, setUserMessage] = useState('');
    const isStreaming = Boolean(messages.find(({ writing }) => writing));
    const resetMessage = () => setUserMessage('');
    const sendMessage = (controlledMessage) => send(controlledMessage || userMessage).then(resetMessage);
    const sendSuggestion = (suggestion) => sendMessage(suggestion);
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !isStreaming) {
            event.preventDefault();
            sendMessage();
        }
    };
    const SUGGESTIONS = [
        t({ comment: 'Review chat - suggestions - explain further', message: 'Explain further' }),
        t({ comment: 'Review chat - suggestions - rewrite clause', message: 'Rewrite clause' }),
        t({
            comment: 'Review chat - suggestions - show playbook reference',
            message: 'Show playbook reference',
        }),
    ];
    return (<Container>
      <Scroller>
        <Thread>
          {messages.map(({ role, message, writing }, i) => (<Message key={
            // eslint-disable-next-line react/no-array-index-key
            i} $direction={role === 'assistant' ? 'left' : 'right'}>
              {role === 'assistant' ? (<StyledAvatarWithLabel solid appearance="assistant" title={t({
                    message: 'Plai',
                    comment: 'Review chat - message - assistant name',
                })}>
                  <Falcon icon="sparkles"/>
                </StyledAvatarWithLabel>) : (<StyledAvatarWithLabel solid appearance="primary" title={userName}>
                  <AvatarLetter>{userName.slice(0, 1)}</AvatarLetter>
                </StyledAvatarWithLabel>)}
              {message === '' ? (<Loader />) : (<Typography>
                  <StyledMarkdown contents={message} $writing={writing ? true : undefined}/>
                </Typography>)}
            </Message>))}
        </Thread>
      </Scroller>
      <SuggestionsSpacer>
        {SUGGESTIONS.map(suggestion => (<Button key={suggestion} size="small" appearance="secondary" onClick={() => sendSuggestion(suggestion)} disabled={isStreaming}>
            {suggestion}
          </Button>))}
      </SuggestionsSpacer>
      <TextAreaSpacer>
        <TextArea onKeyDown={handleKeyDown} value={userMessage} onChange={ev => setUserMessage(ev.target.value)} label={t({ message: 'Ask a question', comment: 'Review chat - textarea - label' })} variant="line" stretch/>
        <Button appearance="primary" onClick={() => sendMessage()} disabled={isStreaming}>
          <Falcon icon="paper-plane"/>
          <Trans comment="Review chat - submit button label">Send</Trans>
        </Button>
      </TextAreaSpacer>
    </Container>);
}
// Exported entry for review chat
export function ReviewChat({ feedbackId }) {
    const userName = useUserName();
    const { messages, chat } = useChatWithDocumentReview(feedbackId);
    const send = (userMessage) => chat(userMessage);
    return <Chat send={send} messages={messages} userName={userName}/>;
}
// Exported entry for playbook chat
export function ReviewChatPlaybook() {
    const userName = useUserName();
    const { messages, chat } = useChatWithPlaybookReview();
    const send = (userMessage) => chat(userMessage);
    return <Chat send={send} messages={messages} userName={userName}/>;
}
